import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  useColorMode,
} from "@chakra-ui/react";
import { BsFileEarmarkText } from "react-icons/bs";
import { getStudyNote } from "../../store/study/thunks";
import {
  selectHasNoteFor,
  selectStudyNoteFor,
} from "../../store/study/selectors";

// Small icon shown next to a ticker when the user has a study note for it.
// Click → Popover lazy-loads the note (if not already in the slice cache) and
// renders the content with an "Open in Study" link. Hidden entirely when no
// note exists, so it doesn't clutter rows for tickers the user hasn't written
// about.
export default function NoteIndicator({
  ticker,
  size = "xs",
  stopPropagation = true,
}) {
  const { colorMode } = useColorMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasNote = useSelector(selectHasNoteFor(ticker));
  const note = useSelector(selectStudyNoteFor(ticker));
  const [opened, setOpened] = useState(false);

  if (!ticker || !hasNote) return null;

  const handleOpen = () => {
    setOpened(true);
    if (!note) {
      dispatch(getStudyNote({ ticker_name: ticker }));
    }
  };

  const handleOpenInStudy = (e) => {
    if (stopPropagation) e.stopPropagation();
    navigate(`/study/${ticker}`);
  };

  return (
    <Popover isLazy placement="top" onOpen={handleOpen} onClose={() => setOpened(false)}>
      <PopoverTrigger>
        <Box
          as="span"
          display="inline-flex"
          onClick={(e) => stopPropagation && e.stopPropagation()}
        >
          <Tooltip label="Has notes — click to preview" hasArrow fontSize="xs">
            <IconButton
              aria-label={`View notes for ${ticker}`}
              icon={<Icon as={BsFileEarmarkText} />}
              size={size}
              variant="ghost"
              colorScheme="blue"
            />
          </Tooltip>
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          maxW="360px"
          bg={colorMode === "light" ? "white" : "gray.800"}
          fontSize="sm"
          onClick={(e) => stopPropagation && e.stopPropagation()}
        >
          <PopoverArrow bg={colorMode === "light" ? "white" : "gray.800"} />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="bold">{ticker} notes</PopoverHeader>
          <PopoverBody>
            {opened && !note ? (
              <Text color="gray.500" fontSize="xs">
                Loading…
              </Text>
            ) : note?.content ? (
              <Text whiteSpace="pre-wrap" noOfLines={10}>
                {note.content}
              </Text>
            ) : (
              <Text color="gray.500" fontSize="xs">
                No content yet.
              </Text>
            )}
            <HStack justify="flex-end" mt={3}>
              <Button size="xs" colorScheme="blue" onClick={handleOpenInStudy}>
                Open in Study
              </Button>
            </HStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
